import { Injectable } from '@angular/core';
import { Feature, FeatureCollection, Geometry, Position } from 'geojson';

import { AssertService } from '@connect-services/client/services/assert.service';

type GeoJSONFeature = Feature<Geometry, Record<string, string | number>>;

enum GeometryTypeEnum {
    LineString = 'LineString',
    MultiPolygon = 'MultiPolygon',
    MultiSurface = 'MultiSurface',
    Point = 'Point',
    Polygon = 'Polygon',
}

interface IsLinearRingClosed {
    isRingClosed?: boolean;
}

export enum GmlPolygonInteriorEnum {
    InnerBoundaryIs = 'innerBoundaryIs',
    Interior = 'interior',
}

enum AxisOrderEnum {
    LAT_LON = 'latlon',
    LON_LAT = 'lonlat',
}

const LINEAR_RING_CLOSURE_TOLERANCE = 1e-9;
const MIN_COORDINATE_IN_POLYGONE = 4;
const WGS84_RE = /(EPSG:?::?4326|opengis\.net\/def\/crs\/EPSG\/\d+\/4326)$/i;

@Injectable({
    providedIn: 'root',
})
export class GmlConverterService {
    private readonly SUPPORTED_GEOMETRY_TAGS: string[] = Object.keys(GeometryTypeEnum);

    public convertGMLToGeoJSON(gml: string): FeatureCollection {
        const parser = new DOMParser();
        const xml: Document = parser.parseFromString(gml, 'application/xml');

        const members: Element[] = [
            ...Array.from(xml.getElementsByTagName('gml:featureMember')),
            ...Array.from(xml.getElementsByTagName('wfs:member')),
        ];

        const features: GeoJSONFeature[] = members.reduce<GeoJSONFeature[]>(
            (acc: GeoJSONFeature[], member: Element): GeoJSONFeature[] => {
                const geomTag: Element | undefined = Array.from(member.getElementsByTagName('*')).find(
                    (element: Element) => this.SUPPORTED_GEOMETRY_TAGS.includes(element.localName),
                );

                if (!geomTag) {
                    return acc;
                }

                const coordinatesNode: Element | undefined = this.findCoordinatesNode(geomTag);

                const geometry: Geometry | null = this.extractGeometryFromTag(
                    geomTag.localName,
                    geomTag,
                    coordinatesNode,
                );

                if (!geometry) {
                    return acc;
                }

                AssertService.assertGeometryType(geometry);

                acc.push({
                    geometry,
                    properties: this.extractProperties(member),
                    type: 'Feature',
                });

                return acc;
            },
            [],
        );

        return {
            features,
            type: 'FeatureCollection',
        };
    }

    private ensureRingIsClosed(ring: number[][]): number[][] {
        if (!ring.length) {
            return ring;
        }

        const firstCoordinate: number[] | undefined = ring.at(0);
        const lastCoordinate: number[] | undefined = ring.at(-1);

        if (!firstCoordinate || !lastCoordinate) {
            return ring;
        }

        const IsLinearRingClosed: boolean =
            Math.abs(firstCoordinate[0] - lastCoordinate[0]) <= LINEAR_RING_CLOSURE_TOLERANCE &&
            Math.abs(firstCoordinate[1] - lastCoordinate[1]) <= LINEAR_RING_CLOSURE_TOLERANCE;

        return IsLinearRingClosed ? ring : [...ring, firstCoordinate];
    }

    private extractGeometryFromTag(
        tagName: string,
        geomTag: Element,
        coordinatesNode?: Element,
    ): GeoJSON.Geometry | null {
        const raw: string = coordinatesNode?.textContent?.trim() || '';
        const order: AxisOrderEnum = this.getAxisOrder(geomTag, raw);

        switch (tagName) {
            case GeometryTypeEnum.Point:
                return this.parsePoint(raw, order);

            case GeometryTypeEnum.LineString:
                return this.parseLineString(geomTag, raw, order);

            case GeometryTypeEnum.Polygon:
                return this.parsePolygon(geomTag, raw, order);

            case GeometryTypeEnum.MultiSurface:
            case GeometryTypeEnum.MultiPolygon:
                return this.parseMultiPolygon(geomTag, order);

            default:
                return null;
        }
    }

    private extractProperties(member: Element): Record<string, string | number> {
        const children: Element[] = Array.from(member.firstElementChild?.children ?? []);

        return children
            .filter((child: Element): boolean => child.tagName !== 'Geom' && !!child.textContent?.trim())
            .reduce((acc: Record<string, string | number>, child: Element): Record<string, string | number> => {
                const text: string = child.textContent!.trim();
                const asNumber: number = +text;

                acc[child.tagName] = Number.isNaN(asNumber) ? text : asNumber;

                return acc;
            }, {});
    }

    private findCoordinatesNode(element?: Element): Element | undefined {
        if (!element) {
            return;
        }

        return (
            element.getElementsByTagNameNS('*', 'posList')[0] ??
            element.getElementsByTagNameNS('*', 'pos')[0] ??
            element.getElementsByTagNameNS('*', 'coordinates')[0] ??
            undefined
        );
    }

    private getAxisOrder(geomTag: Element, raw: string): AxisOrderEnum {
        if (raw.includes(',')) {
            return AxisOrderEnum.LON_LAT;
        }

        const srsName: string = geomTag.closest('[srsName]')?.getAttribute('srsName') ?? '';

        if (WGS84_RE.test(srsName)) {
            return AxisOrderEnum.LAT_LON;
        }

        return AxisOrderEnum.LON_LAT;
    }

    private getCoordinatesText(element: Element | undefined, fallbackRaw: string): string {
        const node: Element | undefined = this.findCoordinatesNode(element);

        return (node?.textContent ?? fallbackRaw).trim();
    }

    private getMultiPolygon(geomTag: Element): Element[] {
        const polygonOrSurfaceMembers: Element[] = [
            ...Array.from(geomTag.getElementsByTagNameNS('*', 'polygonMember')),
            ...Array.from(geomTag.getElementsByTagNameNS('*', 'surfaceMember')),
        ];

        if (polygonOrSurfaceMembers.length) {
            return polygonOrSurfaceMembers.map(
                (member: Element): Element =>
                    member.getElementsByTagNameNS('*', 'Polygon')[0] ||
                    member.getElementsByTagNameNS('*', 'Surface')[0],
            );
        }

        const surfaceMembers: Element = geomTag.getElementsByTagNameNS('*', 'surfaceMembers')[0];

        if (surfaceMembers) {
            return Array.from(surfaceMembers.getElementsByTagNameNS('*', 'Polygon'));
        }

        return Array.from(geomTag.getElementsByTagNameNS('*', 'Polygon'));
    }

    private getPolygonSurface(geomTag: Element, tagName: GmlPolygonInteriorEnum, order: AxisOrderEnum): number[][][] {
        const interiors: Element[] = Array.from(geomTag.getElementsByTagNameNS('*', tagName));

        return interiors
            .map((element: Element): number[][] => {
                const ringEl: Element = element.getElementsByTagNameNS('*', 'LinearRing')[0] ?? element;

                return this.readRing(ringEl, '', order);
            })
            .filter((ring: number[][]) => ring.length >= MIN_COORDINATE_IN_POLYGONE);
    }

    private isAxisOrderSuspicious(points: Position[]): boolean {
        return points.some(([lon, lat]) => Math.abs(lat) > 90 || Math.abs(lon) > 180);
    }

    private parseLinearCoordinates(
        sourceElement: Element | undefined,
        fallbackRaw: string,
        order: AxisOrderEnum,
        { isRingClosed = false }: IsLinearRingClosed = {},
    ): number[][] {
        const text: string = this.getCoordinatesText(sourceElement, fallbackRaw);
        let coordinates: number[][] = this.parseNumericPairs(text, order);

        if (isRingClosed) {
            coordinates = this.ensureRingIsClosed(coordinates);
        }

        return coordinates;
    }

    private parseLineString(geomTag: Element, rawFallback: string, order: AxisOrderEnum): GeoJSON.LineString {
        return {
            coordinates: this.parseLinearCoordinates(geomTag, rawFallback, order),
            type: GeometryTypeEnum.LineString,
        };
    }

    private parseMultiPolygon(geomTag: Element, order: AxisOrderEnum): GeoJSON.MultiPolygon {
        const polygons: Element[] = this.getMultiPolygon(geomTag);

        if (!polygons.length) {
            const linearElement: number[][] = this.parseLinearCoordinates(geomTag, '', order, { isRingClosed: true });

            if (!linearElement.length) {
                return { coordinates: [], type: GeometryTypeEnum.MultiPolygon };
            }

            return { coordinates: [[linearElement]], type: GeometryTypeEnum.MultiPolygon };
        }

        const coordinates: number[][][][] = polygons.map(
            (polygon: Element): Position[][] => this.parsePolygon(polygon, '', order).coordinates,
        );

        return { coordinates, type: GeometryTypeEnum.MultiPolygon };
    }

    private parseNumericPairs(text: string, order: AxisOrderEnum): Position[] {
        const values: number[] = text
            .trim()
            .split(/[,\s]+/)
            .map(Number);

        const points: Position[] = [];
        for (let i = 0; i + 1 < values.length; i += 2) {
            const x: number = values[i],
                y: number = values[i + 1];

            const lon: number = order === AxisOrderEnum.LAT_LON ? y : x;
            const lat: number = order === AxisOrderEnum.LAT_LON ? x : y;
            points.push([lon, lat]);
        }

        return this.isAxisOrderSuspicious(points) ? points.map(([lon, lat]: Position): number[] => [lat, lon]) : points;
    }

    private parsePoint(raw: string, axisOrder: AxisOrderEnum): GeoJSON.Point {
        if (axisOrder === AxisOrderEnum.LON_LAT) {
            const [lon, lat] = raw.split(',').map(Number);

            return {
                coordinates: [lon, lat],
                type: GeometryTypeEnum.Point,
            };
        }

        const [lon, lat] = raw
            .trim()
            .split(/[,\s]+/)
            .map(Number);

        return {
            coordinates: [lat, lon],
            type: GeometryTypeEnum.Point,
        };
    }

    private parsePolygon(geomTag: Element, parentCoordsText: string, order: AxisOrderEnum): GeoJSON.Polygon {
        const outerWrap: Element =
            geomTag.getElementsByTagNameNS('*', 'exterior')[0] ??
            geomTag.getElementsByTagNameNS('*', 'outerBoundaryIs')[0];

        const outerRingElement: Element = outerWrap?.getElementsByTagNameNS('*', 'LinearRing')[0] ?? outerWrap;

        const outer: number[][] = this.readRing(outerRingElement, parentCoordsText, order);

        if (outer.length < MIN_COORDINATE_IN_POLYGONE) {
            return { coordinates: [], type: GeometryTypeEnum.Polygon };
        }

        const holes: number[][][] = [
            ...this.getPolygonSurface(geomTag, GmlPolygonInteriorEnum.Interior, order),
            ...this.getPolygonSurface(geomTag, GmlPolygonInteriorEnum.InnerBoundaryIs, order),
        ];

        return { coordinates: [outer, ...holes], type: GeometryTypeEnum.Polygon };
    }

    private readRing(ringEl: Element | undefined, fallbackRaw: string, order: AxisOrderEnum): number[][] {
        return this.parseLinearCoordinates(ringEl, fallbackRaw, order, { isRingClosed: true });
    }
}
